import React, { ReactElement } from 'react';
import { alpha, Tooltip } from '@mui/material';
import { makeStyles } from 'tss-react/mui';

import { Card } from 'modules/common';
import {
  globalTranslation,
  mergeTranslations,
  useTranslation,
} from 'modules/i18n';
import { ExternalLinkIcon } from 'modules/icons';

import { IDeFiPool } from '../../screens/DeFi/useDeFiPools';
import { translation } from './translation';

const mergedTranslation = mergeTranslations(globalTranslation, translation);

interface IProps {
  data: IDeFiPool;
  secondaryColor?: boolean;
}

export function Pool({ data, secondaryColor }: IProps): ReactElement {
  const { classes, cx } = useStyles();
  const { t, keys } = useTranslation(mergedTranslation);

  return (
    <Card small secondaryColor={secondaryColor}>
      <div className={classes.pool}>
        {/* Tokens */}
        <div className={cx(classes.assets, classes.cell1)}>
          <div className={classes.images}>
            {data.tokens.map((token, index) => (
              <Tooltip key={token.name} title={token.name}>
                <span
                  className={cx(
                    classes.image,
                    {
                      [classes.secondImage]: index === 1,
                    },
                    classes.tokensImage,
                  )}
                >
                  <img alt={token.name} src={token.icon} />
                </span>
              </Tooltip>
            ))}
          </div>

          {data.tokens.map(token => token.name).join(' / ')}
        </div>

        <div className={classes.divider} />

        {/* Protocol */}
        <div className={cx(classes.protocol, classes.cell2)}>
          <div className={classes.description}>{t(keys.protocol)}</div>

          <div className={classes.itemColumn}>
            <div className={classes.item}>
              <Tooltip title={data.protocol.name}>
                <span className={classes.image}>
                  <img alt={data.protocol.name} src={data.protocol.icon} />
                </span>
              </Tooltip>

              <div className={classes.protocolInfo}>
                <div className={classes.protocolName}>{data.protocol.name}</div>

                <div className={classes.protocolType}>{data.protocol.type}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Network */}
        <div className={classes.cell3}>
          <div className={classes.description}>{t(keys.network)}</div>

          <div className={classes.item}>
            <Tooltip title={data.network.name}>
              <span className={classes.image}>
                <img alt={data.network.name} src={data.network.icon} />
              </span>
            </Tooltip>

            <span className={classes.networkText}>{data.network.name}</span>
          </div>
        </div>

        {/* Explore */}
        {data.link ? (
          <a
            className={cx(classes.explore, classes.cell4)}
            href={data.link}
            rel="noreferrer"
            target="_blank"
          >
            <span className={classes.exploreText}>{t(keys.explore)}</span>

            <ExternalLinkIcon className={classes.icon} />
          </a>
        ) : (
          <div className={cx(classes.explore, classes.cell4)}>
            {t(keys.comingSoon)}
          </div>
        )}
      </div>
    </Card>
  );
}

const useStyles = makeStyles()(theme => ({
  pool: {
    display: 'flex',
    alignItems: 'center',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'flex-start',
      position: 'relative',
    },
  },
  images: {
    display: 'flex',
    alignItems: 'center',
  },

  image: {
    '> img': {
      width: 24,
      height: 24,

      [theme.breakpoints.down('sm')]: {
        width: 16,
        height: 16,
      },
    },

    ':hover': {
      transform: 'translateY(-2px)',
    },
  },

  tokensImage: {
    '&& > img': {
      width: 24,
      height: 24,
    },
  },

  secondImage: {
    marginLeft: theme.spacing(-2),
  },

  icon: {
    width: 24,
    height: 24,
  },

  protocol: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(3),

    [theme.breakpoints.down('sm')]: {
      gap: theme.spacing(1.5),
    },
  },

  protocolInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),

    [theme.breakpoints.down('sm')]: {
      flexDirection: 'row',
      alignItems: 'center',
    },
  },

  protocolName: {
    fontSize: 14,

    [theme.breakpoints.down('sm')]: {
      fontSize: 12,
    },
  },

  itemColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },

  protocolType: {
    fontSize: 12,
    color: alpha(theme.palette.text.primary, 0.5),
  },

  explore: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: theme.spacing(1),
    [theme.breakpoints.down('sm')]: {
      position: 'absolute',
      top: 0,
      right: 0,
    },
  },
  exploreText: {
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
  assets: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(3),
  },
  cell1: {
    width: '40%',
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
  },
  cell2: {
    width: '30%',
    [theme.breakpoints.down('sm')]: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      width: '100%',
    },
  },
  cell3: {
    width: '15%',
    [theme.breakpoints.down('sm')]: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      width: '100%',
      marginTop: theme.spacing(3),
    },
  },
  cell4: {
    width: '15%',
    [theme.breakpoints.down('sm')]: {
      width: 'auto',
    },
  },
  divider: {
    height: 1,
    width: '100%',
    backgroundColor: theme.palette.background.default,
    margin: theme.spacing(4, 0),
    [theme.breakpoints.up('sm')]: {
      display: 'none',
    },
  },
  description: {
    color: alpha(theme.palette.secondary.contrastText, 0.5),
    fontSize: 12,
    [theme.breakpoints.up('sm')]: {
      display: 'none',
    },
  },
  item: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(3),

    [theme.breakpoints.down('sm')]: {
      gap: theme.spacing(1.5),
    },
  },
  networkText: {
    [theme.breakpoints.up('sm')]: {
      display: 'none',
    },
  },
}));
